import type { ErrorSummary } from './colour';
import type { ToneMapOptions, ToneMapReport } from './toneMap';

export type ToneKey = 'dark' | 'normal' | 'light';

export type Rgb = [number, number, number];

export interface PaletteBlock {
  id: number;
  meta: number;
  name: string;
  display: string;
  /** The 1.8 blockstate and variant it is drawn with, as `blockstate#variant`. Fluids have none. */
  model?: string;
  recommended: boolean;
  flags: string[];
  note?: string;
}

export interface PaletteColour {
  mcpName: string;
  wikiName: string;
  baseRgb: Rgb;
  baseHex: string;
  mapIndices: Record<ToneKey | 'unused', number>;
  tonesRgb: Record<ToneKey | 'unused', Rgb>;
  blocks: PaletteBlock[];
}

export interface MapartPalette {
  mcVersion: string;
  shadeMultipliers: Record<ToneKey | 'unused', number>;
  colors: Record<string, PaletteColour>;
  invisibleOnMap: { id: number; name: string; note?: string }[];
}

export type StaircaseMode = 'off' | 'classic';

/** A layer setting left at this value takes whatever the build settings say. */
export const FOLLOW_BUILD = 'build';
export type FollowBuild = typeof FOLLOW_BUILD;

/**
 * How one part of the artwork is dithered and built. A layer carries one, and the pixels it ends up
 * owning are quantised and staircased by it instead of by the build settings.
 */
export interface BuildSection {
  /** A `DITHER_METHODS` key, or `FOLLOW_BUILD`. */
  dither: string;
  /** Only read when `dither` names a method; following the build takes the build's strength too. */
  ditherStrength: number;
  staircase: StaircaseMode | FollowBuild;
}

export const FOLLOW_BUILD_SECTION: BuildSection = {
  dither: FOLLOW_BUILD,
  ditherStrength: 1,
  staircase: FOLLOW_BUILD,
};

/** Which section owns each pixel. Entry 0 of `sections` is always `FOLLOW_BUILD_SECTION`. */
export interface SectionMap {
  owners: Uint8Array;
  sections: BuildSection[];
}
export type SupportMode = 'none' | 'gravity' | 'all';
/**
 * Whole artwork as one build, or every map as its own build: tiles staircase from their own
 * noobline and export one file each, named by grid position.
 */
export type ExportMode = 'whole' | 'tiles';

export interface BuildArea {
  x: number;
  z: number;
  width: number;
  length: number;
}

/** One separately built part of the artwork, with heights starting from its own floor. */
export interface BuildRegion extends BuildArea {
  /** Height of this region's noobline row, per column. */
  nooblineHeights: Int32Array;
  nooblineSupportCounts: Uint8Array;
  maxY: number;
}

export type ColourMetric = 'rgb' | 'lab' | 'de2000';

export interface DitherMethod {
  key: string;
  label?: string;
  kind: 'none' | 'diffusion' | 'ordered';
  matrix?: number[][];
  divisor?: number;
}

/** Colour id -> index into that colour's `blocks`, or null when the user disabled the colour. */
export type BlockSelection = Record<number, number | null>;

export interface MapartSettings {
  selection: BlockSelection;
  staircase: StaircaseMode;
  support: SupportMode;
  supportBlock: { id: number; meta: number };
  /**
   * The row of support blocks north of the artwork, which is what the top row's shade is measured
   * against. Off, that row shades against whatever stands north of it in the world.
   */
  noobline: boolean;
  dither: string;
  metric: ColourMetric;
  serpentine: boolean;
  /** 0 turns dithering off without forgetting which method was picked. */
  ditherStrength: number;
  toneMap: ToneMapOptions;
  exportMode: ExportMode;
  alphaThreshold: number;
}

/** One entry of the palette the quantiser is allowed to choose from. */
export interface PaletteEntry {
  colourId: number;
  tone: ToneKey;
  rgb: Rgb;
  lab: Rgb;
  block: PaletteBlock;
}

export interface MaterialCount {
  id: number;
  meta: number;
  display: string;
  count: number;
}

export interface BuildWarning {
  key: string;
  args?: (string | number)[];
}

export interface BuildResult {
  width: number;
  length: number;
  /** Per pixel index into the reduced palette, -1 for transparent. */
  entries: Int32Array;
  /** Tones the reduced palette was built with: classic as soon as one section staircases. */
  paletteMode: StaircaseMode;
  /** The whole artwork as one region, or one region per map in tile mode. */
  regions: BuildRegion[];
  /** Per pixel build height, relative to the floor of the region the pixel belongs to. */
  heights: Int32Array;
  /** Support blocks needed beneath each pixel. */
  supportCounts: Uint8Array;
  /** Per pixel `baseColourIndex * 4 + shade`, what the server would send for this build. */
  mapBytes: Uint8Array;
  previewRgba: Uint8ClampedArray;
  minY: number;
  /** Top of the tallest region. */
  maxY: number;
  blockCount: number;
  supportCount: number;
  /** How far the render lands from the source, in CIEDE2000 units. */
  error: ErrorSummary;
  /** What tone mapping had to change, or null when it was off. */
  tone: ToneMapReport | null;
  /** Size of the dense MCEdit block+data arrays the largest exported file would need. */
  schematicBytes: number;
  materials: MaterialCount[];
  warnings: BuildWarning[];
}
